/**
 * Accounts — Learn-Mode inline tips.
 *
 * Anchored across `routes/social/Accounts.tsx` and
 * `routes/social/_sections/accounts/AccountCard.tsx`.
 */

import type { LearnTip } from "./types";

export const accountsTips: LearnTip[] = [
  {
    key: "social-accounts-onboarding",
    title: "Connecting Your First Account",
    body: "Connect a Facebook Page to get started — we'll automatically add any Instagram Business accounts linked to it, too.",
  },
  {
    key: "social-accounts-health-summary",
    title: "Accounts Needing Attention",
    body: "A summary of connections that need a person to step in — an expired sign-in to reconnect, or a sign-in that's about to expire within 14 days.",
  },
  {
    key: "social-accounts-connect",
    title: "Adding Another Account",
    body: "New accounts you connect are linked to whichever client is selected in the top-nav switcher. Pick the right client first, or choose “All clients” to add the account at the agency level and assign it later.",
  },
  {
    key: "social-accounts-bluesky-connect",
    title: "Connecting Bluesky",
    body: "Bluesky connects with your handle and an app password — never your main password. Create one in Bluesky under Settings → Privacy and security → App passwords. It's used once to sign in and isn't stored; revoke it in Bluesky any time to disconnect.",
  },
  {
    key: "social-accounts-list",
    title: "Connected Accounts",
    body: "Every social account currently linked to your organization, grouped by client. A colored indicator on each card shows whether the connection is healthy, expiring, or needs reconnecting.",
  },
  {
    key: "social-accounts-assign-client",
    title: "Assigning an Account",
    body: "This account isn't linked to a client, so its posts and health don't show on any client. Pick the client it belongs to and choose Assign — you can move it again later.",
  },
  {
    key: "social-accounts-card",
    title: "Account Status",
    body: "Shows this account's connection health and which client it's linked to. If it needs attention, the card explains why and offers a way to fix it (usually reconnecting).",
  },
  {
    key: "social-connect-invite-open",
    title: "Invite a Client to Connect",
    body: "Create a link your client opens to connect their own Facebook, Instagram, TikTok, LinkedIn and other accounts — no Animas login and no passwords shared with you. You copy the link and send it yourself.",
  },
  {
    key: "social-connect-invite-dialog",
    title: "Client Connect Links",
    body: "Pick which platforms the client may connect and how long the link lasts. Accounts they connect land under this client; a Page already linked to a different client is skipped, never moved.",
  },
  {
    key: "social-connect-invite-create",
    title: "Creating the Link",
    body: "Only platforms whose sign-in app is set up on this workspace can be offered. By default each platform can be connected once before the link stops working.",
  },
  {
    key: "social-connect-invite-link",
    title: "Copy It Now",
    body: "The link is shown only once. Anyone holding it can connect accounts to this client until it expires, so send it only to the client — and revoke it if it goes to the wrong person.",
  },
  {
    key: "social-connect-invite-list",
    title: "Link Status and Activity",
    body: "Each link shows whether it's active, used, expired or revoked, plus a timeline of when the client opened it and what connected (or why something didn't). Revoke stops the link immediately.",
  },
  {
    key: "social-client-invite-connect",
    title: "Invite This Client",
    body: "Send this client a link to connect their own social accounts. Useful when a client has no accounts connected yet.",
  },
  {
    key: "social-client-connect",
    title: "Client Connect Page",
    body: "What your client sees when they open a connect link: your client's name and logo, and one button per platform you allowed.",
  },
  {
    key: "social-client-connect-platforms",
    title: "Platform Buttons",
    body: "Each button sends the client to that platform's own sign-in page. They grant access there; we never see their password.",
  },
  {
    key: "social-client-connect-pick",
    title: "Choosing Pages",
    body: "Facebook and LinkedIn sign-ins can manage several Pages. The client ticks only the ones that belong to their business; nothing else is connected.",
  },
  {
    key: "social-clients-not-on-social",
    title: "Clients Not on Social",
    body: "These clients have a brand on file but aren't managed for social media, so they're hidden from the client pickers on web and mobile. Add one to social when you start managing its accounts.",
  },
  {
    key: "social-client-remove-from-social",
    title: "Remove From Social",
    body: "Hides this client from the social client pickers without deleting anything. Their brand details, connected accounts and post history stay in place, and you can add them back to social at any time.",
  },
];
